import { type AppStore, useAppStore } from '@/inertia/AppStore';
import FormGating from '@/inertia/Shared/FormGating';
import { Box, type BoxProps, Skeleton, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlayerElement, PlayerElementFallback } from './PlayerElement';

import { Analytics } from '@/Telemetry';
import { getTextFromHtmlString } from '@/Utility/utils';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { createStore } from 'jotai/vanilla';
import PlayerBar from './PlayerBar';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import './Player.less';

const store = createStore();
const playerAtom = atom({ current: null }, (get, set, update) => {
  set(playerAtom, update);
});
store.set(playerAtom, { current: null });

export const usePlayerRef = () => useAtomValue(playerAtom) as { current: HTMLDivElement | null };
export const getPlayerRef = () => store.get(playerAtom) as { current: HTMLDivElement | null };

type AppStoreProps = {
  node: AppStore['playerNode'];
};

const AiExtractiveAnswer = ({ node }: AppStoreProps) => {
  const extractiveAnswers = node?.searchResults?.extractiveAnswers;
  if (!extractiveAnswers?.length) return null;

  const answer = extractiveAnswers?.[0]?.content;
  if (!answer) return null;

  return (
    <div className="AiExtractiveAnswer">
      <Typography variant="body2" component={'div'}>
        <div dangerouslySetInnerHTML={{ __html: `<b>Ai Extract: </b>: ${answer}` }} />
      </Typography>
    </div>
  );
};

type AiSummaryToggleButtonProps = {
  aiSummary: string | null;
  onToggle: (show: boolean) => void;
};

const AiSummaryToggleButton = ({ aiSummary, onToggle }: AiSummaryToggleButtonProps) => {
  const [showAiSummary, setShowAiSummary] = useState(false);

  if (!aiSummary) return null;

  const handleToggle = () => {
    const newShowAiSummary = !showAiSummary;
    setShowAiSummary(newShowAiSummary);
    onToggle(newShowAiSummary);
  };

  return (
    <button type="button" className="SummaryToggle" onClick={handleToggle}>
      <Typography variant="body2" fontWeight={500}>
        {showAiSummary ? 'Show Description' : 'Show AI Summary'}
      </Typography>
    </button>
  );
};

const CtaButton = ({ node }: AppStoreProps) => {
  const link = node?.znglyContent?.ctaLink;
  const text = node?.znglyContent?.ctaName;

  if (!link || !text) return null;

  return (
    <a
      target="_blank"
      rel="noreferrer noopener"
      className="CtaButton"
      href={link}
      onClick={() => {
        Analytics.push('zngly_media_interaction', {
          eventaction: 'cta_click',
          eventlabel: 'zngly_cta',
        });
      }}
    >
      <Typography variant="body2" fontWeight={500}>
        {text}
      </Typography>
    </a>
  );
};

type Props = {
  playerProps?: BoxProps;
  containerProps?: BoxProps;
  wrapperProps?: BoxProps;
  detailsProps?: BoxProps;
};

const Player = ({ id, ...props }: { id?: string } & Props) => {
  const node = useAppStore((state) => state?.playerNode);
  const pageType = useAppStore((state) => state?.pageType);
  const [showMore, setShowMore] = useState(false);
  const [componentRendered, setComponentRendered] = useState(false);

  const playerRef = useRef<HTMLDivElement>(null);
  const setPlayerRef = useSetAtom(playerAtom);

  const [showAiSummary, setShowAiSummary] = useState(false);
  const aiSummary = node?.znglyContent?.aiSummary;

  const key = `${pageType}_${id}`;

  // set the player ref in jotai store
  useEffect(() => {
    setPlayerRef(playerRef);
    store.set(playerAtom, playerRef);
  }, [playerRef.current]);

  useEffect(() => {
    setShowMore(false);
  }, [node?.id]);

  useEffect(() => {
    setComponentRendered(true);

    return () => {
      setComponentRendered(false);
    };
  }, []);

  const contentDivRef = useRef<HTMLDivElement>(null);

  const canShowMore: boolean = useMemo(() => {
    if (!contentDivRef.current) return false;
    const content = showAiSummary
      ? (node?.znglyContent?.aiSummary ?? '')
      : (node?.znglyContent?.htmlDesc ?? node?.znglyContent?.shortText ?? '');
    const { text, childrenLength } = getTextFromHtmlString(content);

    if (childrenLength > 2) return true;

    const divWidth = contentDivRef.current.offsetWidth;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const font = window.getComputedStyle(contentDivRef.current).font;

    if (!context) return false;
    context.font = font;
    const textWidth = context.measureText(text).width;

    canvas.remove(); // cleanup

    const lines = textWidth / divWidth;
    return lines > 1.8;
  }, [
    contentDivRef?.current,
    showAiSummary,
    node?.znglyContent?.aiSummary,
    node?.znglyContent?.htmlDesc,
    node?.znglyContent?.shortText,
    node?.id,
    componentRendered,
  ]);

  return (
    <Box className="Player" ref={playerRef} key={key} {...props.playerProps}>
      {/* Main Content shown here */}
      <Box
        className="Container"
        {...props?.containerProps}
        sx={{
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          paddingTop: '56.25%',
          ...props?.containerProps?.sx,
        }}
      >
        <FormGating key={key} location="player" />
        <Box
          className="Wrapper"
          {...props?.wrapperProps}
          sx={{
            position: 'absolute',
            top: '0',
            left: '0',
            bottom: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            ...props?.wrapperProps?.sx,
          }}
        >
          <PlayerElement node={node} />
          <PlayerBar node={node} />
        </Box>
      </Box>
      <Box
        className="Details"
        {...props?.detailsProps}
        sx={{
          backgroundColor: 'var(--description-box-col)',
          color: 'var(--description-box-text-col)',
          padding: '0.5em 1em',
          ...props?.detailsProps?.sx,
        }}
      >
        <div className="TitleContainer">
          <div className="Title">
            <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
              {node?.title}
            </Typography>
          </div>
          <div className="ButtonContainer">
            <AiSummaryToggleButton
              aiSummary={aiSummary || null}
              onToggle={(show) => {
                setShowAiSummary(show);
                setShowMore(show); // Show full summary when AI summary is toggled
              }}
            />
            <CtaButton node={node} />
          </div>
        </div>
        <AiExtractiveAnswer node={node} />
        <div className="Description">
          <Typography variant="body2" component={'div'}>
            <div
              ref={contentDivRef}
              style={{
                maxHeight: showMore ? 'none' : '60px',
                transition: 'max-height 0.5s ease',
                overflow: 'hidden',
              }}
            >
              {showAiSummary ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => (
                      <Typography variant="h4" sx={{ mt: 3, mb: 2 }}>
                        {children}
                      </Typography>
                    ),
                    h2: ({ children }) => (
                      <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
                        {children}
                      </Typography>
                    ),
                    h3: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        {children}
                      </Typography>
                    ),
                    p: ({ children }) => (
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {children}
                      </Typography>
                    ),
                    strong: ({ children }) => (
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {children}
                      </Typography>
                    ),
                    ul: ({ children }) => (
                      <Box component="ul" sx={{ pl: 3, mt: 1, mb: 1 }}>
                        {children}
                      </Box>
                    ),
                    ol: ({ children }) => (
                      <Box component="ol" sx={{ pl: 3, mt: 1, mb: 1 }}>
                        {children}
                      </Box>
                    ),
                    li: ({ children }) => (
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">{children}</Typography>
                      </Box>
                    ),
                  }}
                >
                  {aiSummary}
                </ReactMarkdown>
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: node?.znglyContent?.htmlDesc ?? node?.znglyContent?.shortText ?? '',
                  }}
                />
              )}
            </div>
            {canShowMore && !showAiSummary && (
              <div className="ReadMore">
                <button type="button" onClick={() => setShowMore(!showMore)}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {showMore ? 'Show Less' : 'Show More'}
                  </Typography>
                </button>
              </div>
            )}
          </Typography>
        </div>
      </Box>
    </Box>
  );
};

const PlayerFallback = () => {
  return (
    <div className="Player Fallback">
      {/* Main Content shown here */}
      <div
        className="Container"
        style={{ position: 'relative', width: '100%', overflow: 'hidden', paddingTop: '56.25%' }}
      >
        <div
          className="Wrapper"
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            bottom: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <PlayerElementFallback />
        </div>
      </div>
      <div
        className="Details"
        style={{
          backgroundColor: 'var(--description-box-col)',
          color: 'var(--description-box-text-col)',
          padding: '0.5em 1em',
        }}
      >
        <div className="TitleContainer">
          <div className="Title">
            <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
              <Skeleton variant="text" width="100%" />
            </Typography>
          </div>
        </div>

        <div className="Description">
          <Typography variant="body2" component={'div'}>
            <div>
              <Skeleton variant="text" width="100%" />
            </div>
          </Typography>
        </div>
      </div>
    </div>
  );
};

// trigger re-render when node changes - weird bug where player appears blank when node changes to youtube
const PlayerWrapper = ({ isFallback, ...props }: { isFallback?: boolean } & Props) => {
  const node = useAppStore((state) => state?.playerNode);
  const pageType = useAppStore((state) => state?.pageType);

  if (isFallback) {
    return <PlayerFallback />;
  }

  return <Player key={`${pageType}_${node?.id}`} id={node?.id} {...props} />;
};

export default PlayerWrapper;
